import { useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Archive, ArrowDown, ArrowUp, Copy, Edit3, Package, Plus, Trash2, Undo2 } from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Skeleton,
  StatusPill,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { paiseOrDash } from "../format-money.ts";
// This card *is* order revenue, so it uses the same no-decimals formatter as the
// order and quote rails rather than a second one that rounds differently.
import { orderMoney } from "../orders/order-format.ts";
import { usePermission } from "../../../lib/permissions.ts";
import {
  PHASE_LABEL,
  PHASE_TONE,
  TYPE_LABEL,
  phaseWithDates,
  seasonWindow,
} from "./collection-format.ts";
import { AddProductsDialog } from "./add-products-dialog.tsx";
import { DuplicateCollectionDialog } from "./duplicate-collection-dialog.tsx";

const LISTING_TONE = { published: "ok", draft: "muted", archived: "bad" } as const;

/**
 * One label/value line in a rail card — the shape the Payment and Value cards on
 * the order and quote screens already use, so the three rails read alike.
 * Numbers are mono + tabular per the brand spec; the ₹ inside `orderMoney` stays
 * sans, which is the store's own house rule.
 */
function RailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted">{label}</span>
      <span className="font-mono tabular-nums text-ink">{value}</span>
    </div>
  );
}

/**
 * Store admin — Collection detail (Commerce, M6). Membership is curated here.
 * @see prototype/store/admin/all.html#collections-detail
 */
export function CollectionDetailPage({ collectionId }: { collectionId: string }) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const q = trpc.storeCollections.get.useQuery({ collectionId });
  // Its own query, not a field on `get`: the page paints without waiting on an
  // order-lines aggregate, and the card refreshes independently.
  const perf = trpc.storeCollections.performance.useQuery({ collectionId });
  const [addOpen, setAddOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [bulkRemoveOpen, setBulkRemoveOpen] = useState(false);
  // Bulk-select for Remove. Cleared whenever the member list changes.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * Listing ids in the order the operator just dragged them into, held until
   * the server confirms. Null means "trust the server's order" — cleared on
   * success once the refetch carries the same sequence, and on failure so the
   * rows snap back rather than lying about what was saved.
   */
  const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);
  /** Spoken after a reorder — the visual result is obvious, the audible one isn't. */
  const [announcement, setAnnouncement] = useState("");
  /** True while the bulk-remove loop runs, so each row doesn't refresh and toast on its own. */
  const bulkRemoving = useRef(false);
  const canManage = usePermission("store.catalog.manage");

  const invalidate = async () => {
    setSelected(new Set());
    await Promise.all([
      utils.storeCollections.get.invalidate({ collectionId }),
      utils.storeCollections.list.invalidate(),
      utils.storeCollections.counts.invalidate(),
      // Performance is scoped to the sheet's *current* membership, so adding or
      // removing a style changes it. Without this the card keeps showing the
      // previous line-up's revenue.
      utils.storeCollections.performance.invalidate({ collectionId }),
    ]);
  };

  const addListings = trpc.storeCollections.addListings.useMutation({
    onSuccess: async (r) => {
      await invalidate();
      setAddOpen(false);
      toast.success(`Added to ${c?.name ?? "collection"}`, {
        description: `${r.productCount} ${r.productCount === 1 ? "product" : "products"} in this collection.`,
      });
    },
    onError: (e) => toast.error("Couldn't add products", { description: storeErrorMessage(e) }),
  });
  const removeListing = trpc.storeCollections.removeListing.useMutation({
    onSuccess: async () => {
      // A bulk remove drives this one row at a time, and doing the full refresh
      // and a toast per row means eight products cost eight refetch storms and
      // eight stacked toasts. The loop reports once, at the end, for all of them.
      if (bulkRemoving.current) return;
      await invalidate();
      toast.success("Product removed");
    },
    onError: (e) => toast.error("Couldn't remove product", { description: storeErrorMessage(e) }),
  });
  const restore = trpc.storeCollections.restore.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Collection restored");
    },
    onError: (e) => toast.error("Couldn't restore", { description: storeErrorMessage(e) }),
  });
  const reorder = trpc.storeCollections.reorder.useMutation({
    onSuccess: async () => {
      await invalidate();
      setPendingOrder(null);
    },
    onError: (e) => {
      setPendingOrder(null);
      toast.error("Couldn't save the new order — restored.", {
        description: storeErrorMessage(e),
      });
    },
  });
  const duplicate = trpc.storeCollections.duplicate.useMutation({
    onSuccess: async (r) => {
      await utils.storeCollections.list.invalidate();
      await utils.storeCollections.counts.invalidate();
      setDuplicateOpen(false);
      toast.success("Collection duplicated", {
        description: "Same products, same order — check its dates and publish.",
      });
      await navigate({
        to: "/store/admin/collections/$collectionId",
        params: { collectionId: r.collectionId },
      });
    },
    onError: (e) => toast.error("Couldn't duplicate", { description: storeErrorMessage(e) }),
  });
  const archive = trpc.storeCollections.archive.useMutation({
    onSuccess: async () => {
      await invalidate();
      toast.success("Collection archived");
      await navigate({ to: "/store/admin/collections" });
    },
    onError: (e) => toast.error("Couldn't archive", { description: storeErrorMessage(e) }),
  });

  const c = q.data;

  /**
   * The line sheet in the order to paint it — the optimistic sequence when a
   * reorder is in flight, the server's otherwise. Ids that vanished between the
   * drag and the refetch are dropped rather than rendered as holes.
   */
  const members = (() => {
    if (!c) return [];
    if (!pendingOrder) return c.members;
    const byId = new Map(c.members.map((m) => [m.listingId, m]));
    const ordered = pendingOrder.flatMap((id) => {
      const m = byId.get(id);
      return m ? [m] : [];
    });
    const seen = new Set(ordered.map((m) => m.listingId));
    return [...ordered, ...c.members.filter((m) => !seen.has(m.listingId))];
  })();

  /** Swap a row with its neighbour and persist the whole sheet's new order. */
  const move = (index: number, delta: -1 | 1) => {
    const next = members.map((m) => m.listingId);
    const target = index + delta;
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[target] = a;
    setPendingOrder(next);
    // The row moves and the focused button moves with it, so a screen reader
    // gets no signal that anything happened — design S-CL3 makes the
    // announcement part of the keyboard path, not an extra.
    const moved = members[index];
    if (moved) setAnnouncement(`${moved.name} moved to position ${target + 1} of ${next.length}.`);
    reorder.mutate({ collectionId, listingIds: next });
  };

  const phaseHint = c ? phaseWithDates(c.phase, c.activeFrom, c.activeTo) : null;

  // Spec 15 R4's collection half. The listing half is the published count below —
  // a draft style is a member of the sheet but never reaches a buyer.
  const buyerVisible = c?.status === "active" && (c.phase === "upcoming" || c.phase === "live");
  const publishedCount = c?.members.filter((m) => m.visibility === "published").length ?? 0;
  const unpublishedCount = (c?.members.length ?? 0) - publishedCount;

  if (!c) {
    return (
      <StoreAdminLayout
        breadcrumbs={[
          { label: "Store", to: "/store/admin" },
          { label: "Collections", to: "/store/admin/collections" },
          { label: "…" },
        ]}
      >
        <StorePageColumn>
          <BackButton fallbackTo="/store/admin/collections" label="Back to collections" />
          <div className="mt-4">
            {q.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <p className="text-sm text-muted">Collection not found.</p>
            )}
          </div>
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Collections", to: "/store/admin/collections" },
        { label: c.name },
      ]}
    >
      {/* A table beside the rail, not a form — see `StorePageColumn`'s docblock
          for why those want opposite widths. New/edit stay on the default. */}
      <StorePageColumn variant="grid">
        <p role="status" aria-live="polite" className="sr-only">
          {announcement}
        </p>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line pb-5">
          <div className="flex items-start gap-4">
            <BackButton
              fallbackTo="/store/admin/collections"
              label="Back to collections"
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
                  {c.name}
                </h1>
                <StatusPill
                  tone={PHASE_TONE[c.phase]}
                  size="sm"
                  dot
                  {...(phaseHint ? { title: phaseHint } : {})}
                >
                  {PHASE_LABEL[c.phase]}
                </StatusPill>
                {/* Inline from tablet up, tooltip-only on mobile where the
                    header already competes for the line. */}
                {phaseHint ? (
                  <span className="hidden text-sm text-muted md:inline">{phaseHint}</span>
                ) : null}
              </div>
              <p className="mt-1 text-sm text-muted">
                {TYPE_LABEL[c.type]} · {c.members.length}{" "}
                {c.members.length === 1 ? "product" : "products"} ·{" "}
                {seasonWindow(c.activeFrom, c.activeTo)}
              </p>
            </div>
          </div>
          {canManage ? (
            <div className="flex items-center gap-2">
              <Button asChild intent="primary" size="md">
                <Link to="/store/admin/collections/$collectionId/edit" params={{ collectionId }}>
                  <Edit3 size={14} aria-hidden />
                  Edit
                </Link>
              </Button>
              <Button
                intent="secondary"
                size="md"
                disabled={duplicate.isPending}
                onClick={() => setDuplicateOpen(true)}
              >
                <Copy size={14} aria-hidden />
                Duplicate
              </Button>
              {c.status === "archived" ? (
                <Button
                  intent="secondary"
                  size="md"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate({ collectionId })}
                >
                  <Undo2 size={14} aria-hidden />
                  Restore
                </Button>
              ) : (
                <Button intent="secondary" size="md" onClick={() => setArchiveOpen(true)}>
                  <Archive size={14} aria-hidden />
                  Archive
                </Button>
              )}
            </div>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_320px]">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-ink">
                  Products in this collection
                  {selected.size > 0 ? (
                    <span className="ml-2 font-normal text-muted">{selected.size} selected</span>
                  ) : null}
                </h2>
                {canManage ? (
                  <div className="flex items-center gap-2">
                    {selected.size > 0 ? (
                      <Button
                        intent="secondary"
                        size="sm"
                        disabled={removeListing.isPending}
                        onClick={() => setBulkRemoveOpen(true)}
                      >
                        <Trash2 size={14} aria-hidden />
                        Remove {selected.size}
                      </Button>
                    ) : null}
                    <Button intent="secondary" size="sm" onClick={() => setAddOpen(true)}>
                      <Plus size={14} aria-hidden />
                      Add products
                    </Button>
                  </div>
                ) : (
                  // Spec 15 AC9 / design S-CL3: present but disabled with the
                  // reason, so a view-only operator learns the control exists
                  // and why it is shut, rather than wondering where it went.
                  // The per-row reorder handles stay hidden — greying every row
                  // is noise, and the button already carries the explanation.
                  <Button
                    intent="secondary"
                    size="sm"
                    disabled
                    title="Needs catalog manage permission"
                  >
                    <Plus size={14} aria-hidden />
                    Add products
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody>
              {c.members.length === 0 ? (
                <p className="py-10 text-center text-sm text-muted">
                  No products yet. Add styles in the order buyers should see them.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  {/* Raised from 520px: the row now carries a position, a
                      thumbnail, two reorder controls and Remove, and at 520 the
                      text columns were the ones that gave way. Below this the
                      card scrolls horizontally rather than crushing them. */}
                  <table className="w-full min-w-[680px] text-left text-sm">
                    <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
                      <tr>
                        {canManage ? (
                          <th className="w-10 px-3 py-2">
                            <input
                              type="checkbox"
                              aria-label="Select all products"
                              className="h-4 w-4 accent-sky-600"
                              checked={selected.size > 0 && selected.size === c.members.length}
                              // Indeterminate is a property, not an attribute —
                              // it can only be set through the node.
                              ref={(el) => {
                                if (el)
                                  el.indeterminate =
                                    selected.size > 0 && selected.size < c.members.length;
                              }}
                              onChange={(e) =>
                                setSelected(
                                  e.target.checked
                                    ? new Set(c.members.map((m) => m.listingId))
                                    : new Set(),
                                )
                              }
                            />
                          </th>
                        ) : null}
                        <th className="w-10 px-3 py-2 text-right font-medium" title="Line-sheet position">
                          #
                        </th>
                        <th className="w-12 px-3 py-2">
                          <span className="sr-only">Image</span>
                        </th>
                        {/* The only column that may wrap — style names are the
                            one genuinely variable-length field here. */}
                        <th className="w-full px-3 py-2 text-left font-medium">Product</th>
                        <th className="px-3 py-2 text-left font-medium">SKU</th>
                        <th className="px-3 py-2 text-right font-medium">Base price</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                        <th className="px-3 py-2 text-center font-medium">
                          {canManage ? "Actions" : <span className="sr-only">Actions</span>}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m, i) => (
                        <tr key={m.listingId} className="border-b border-line last:border-0">
                          {canManage ? (
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                aria-label={`Select ${m.name}`}
                                className="h-4 w-4 accent-sky-600"
                                checked={selected.has(m.listingId)}
                                onChange={() =>
                                  setSelected((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(m.listingId)) next.delete(m.listingId);
                                    else next.add(m.listingId);
                                    return next;
                                  })
                                }
                              />
                            </td>
                          ) : null}
                          {/* The stored position, not the row index: if the two
                              ever disagree the sequence is not contiguous and
                              R5 is broken, which should be visible rather than
                              papered over by renumbering on render. During an
                              optimistic reorder the server value is stale, so
                              the pending index wins until it lands. */}
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-muted">
                            {pendingOrder ? i + 1 : m.sortOrder}
                          </td>
                          <td className="px-3 py-2">
                            {m.images[0] ? (
                              <img
                                src={m.images[0]}
                                alt=""
                                className="h-8 w-8 rounded-sm border border-line object-cover"
                              />
                            ) : (
                              <span className="flex h-8 w-8 items-center justify-center rounded-sm border border-line bg-raised text-muted">
                                <Package className="h-4 w-4" aria-hidden />
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Link
                              to="/store/admin/products/$listingId"
                              params={{ listingId: m.listingId }}
                              className="font-medium text-ink hover:text-sky-700"
                            >
                              {m.name}
                            </Link>
                          </td>
                          {/* A SKU is one token — breaking it across lines
                              ("SKU- / COT- / 002") makes it unreadable and
                              unscannable down the column. */}
                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted">
                            {m.sku ?? "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-ink">
                            {paiseOrDash(m.basePricePaise)}
                          </td>
                          <td className="px-3 py-2">
                            <StatusPill tone={LISTING_TONE[m.visibility]} size="sm" dot>
                              {m.visibility}
                            </StatusPill>
                          </td>
                          <td className="px-3 py-2">
                            {canManage ? (
                              <div className="flex items-center justify-center gap-1">
                                {/* Buttons rather than drag: keyboard and screen
                                    readers get the same path as the mouse, with
                                    no DnD dependency. */}
                                <Button
                                  intent="ghost"
                                  size="sm"
                                  aria-label={`Move ${m.name} up`}
                                  className="h-7 w-7 px-0"
                                  disabled={i === 0 || reorder.isPending}
                                  onClick={() => move(i, -1)}
                                >
                                  <ArrowUp size={14} aria-hidden />
                                </Button>
                                <Button
                                  intent="ghost"
                                  size="sm"
                                  aria-label={`Move ${m.name} down`}
                                  className="h-7 w-7 px-0"
                                  disabled={i === members.length - 1 || reorder.isPending}
                                  onClick={() => move(i, 1)}
                                >
                                  <ArrowDown size={14} aria-hidden />
                                </Button>
                                <Button
                                  intent="secondary"
                                  size="sm"
                                  disabled={removeListing.isPending}
                                  onClick={() =>
                                    removeListing.mutate({ collectionId, listingId: m.listingId })
                                  }
                                >
                                  <Trash2 size={14} aria-hidden />
                                  Remove
                                </Button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* The wireframe drew Performance and Applied-to here. Performance is
              real now. Applied-to is gone: it was designed around buyer-tier
              price lists and a discount code, and product/19 later cancelled the
              offers engine outright ("no coupon codes ever") and parked tier
              pricing behind buyer accounts — so it advertised features that
              aren't coming. Buyer visibility answers a question the operator
              actually has, from data this page already holds. */}
          <div className="grid gap-5 lg:content-start">
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Performance</h2>
              </CardHeader>
              <CardBody>
                {perf.isError ? (
                  <p className="text-sm text-muted">Couldn&rsquo;t load sales for this collection.</p>
                ) : (
                  <>
                    <RailRow
                      label="Revenue"
                      value={perf.data ? orderMoney(perf.data.revenuePaise) : "—"}
                    />
                    <RailRow
                      label="Units sold"
                      value={perf.data ? `${perf.data.packs.toLocaleString("en-IN")} packs` : "—"}
                    />
                    <RailRow
                      label="Pieces"
                      value={perf.data ? perf.data.pieces.toLocaleString("en-IN") : "—"}
                    />
                    <RailRow
                      label="Orders"
                      value={perf.data ? perf.data.orderCount.toLocaleString("en-IN") : "—"}
                    />
                    <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                      Confirmed, approved and fulfilled orders, excluding tax. Counts the styles
                      currently in this collection — so a style added today brings its past orders
                      with it, and a style in two collections counts in both.
                    </p>
                  </>
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Buyer visibility</h2>
              </CardHeader>
              <CardBody>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Reaches buyers</span>
                  <StatusPill tone={buyerVisible ? "ok" : "muted"} size="sm" dot>
                    {buyerVisible ? "Yes" : "No"}
                  </StatusPill>
                </div>
                <RailRow
                  label="Styles buyers see"
                  value={`${publishedCount} of ${c.members.length}`}
                />
                <p className="mt-3 border-t border-line pt-3 text-xs text-muted">
                  {!buyerVisible
                    ? c.status === "archived"
                      ? "Archived collections never reach buyers."
                      : "An ended season is hidden from buyers. Extend its window to reopen it."
                    : c.members.length === 0
                      ? // "Every style is published" is a silly thing to say about none.
                        "Add styles and buyers will see them here."
                      : unpublishedCount > 0
                        ? `${unpublishedCount} ${unpublishedCount === 1 ? "style isn't" : "styles aren't"} published, so buyers won't see ${unpublishedCount === 1 ? "it" : "them"} here.`
                        : "Every style in this collection is published."}
                </p>
              </CardBody>
            </Card>
          </div>
        </div>
      </StorePageColumn>

      <AddProductsDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        alreadyIn={c.members.map((m) => m.listingId)}
        busy={addListings.isPending}
        onAdd={(listingIds) => addListings.mutate({ collectionId, listingIds })}
      />

      <ConfirmDialog
        open={bulkRemoveOpen}
        onOpenChange={setBulkRemoveOpen}
        title={`Remove ${selected.size} ${selected.size === 1 ? "product" : "products"}?`}
        description="They come off this collection only — the products themselves are not changed, and you can add them back at any time."
        confirmLabel="Remove"
        pending={removeListing.isPending}
        onConfirm={async () => {
          // One call per row: `removeListing` is per-product, and a bulk
          // endpoint isn't worth it for a table capped at a page of members.
          // The per-row refresh and toast are suppressed for the run, so the
          // cost is N requests rather than N requests × 4 invalidations × a toast.
          const count = selected.size;
          bulkRemoving.current = true;
          try {
            for (const listingId of selected) {
              await removeListing.mutateAsync({ collectionId, listingId });
            }
            toast.success(`${count} ${count === 1 ? "product" : "products"} removed`);
            setBulkRemoveOpen(false);
          } finally {
            // Refresh even if a row failed part-way: the ones before it are
            // already gone, and leaving the table showing them is worse than
            // the error itself. `onError` has already said what went wrong.
            bulkRemoving.current = false;
            await invalidate();
          }
        }}
      />

      <DuplicateCollectionDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        defaultName={`${c.name} (copy)`}
        seasonal={c.type === "seasonal"}
        busy={duplicate.isPending}
        onConfirm={(v) => duplicate.mutate({ collectionId, ...v })}
      />

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title="Archive this collection?"
        description={`“${c.name}” will be hidden from the collections list. Its products are not affected, and you can restore it later.`}
        confirmLabel="Archive"
        pending={archive.isPending}
        onConfirm={() => archive.mutate({ collectionId })}
      />
    </StoreAdminLayout>
  );
}
